if (!customElements.get('range-filter')) {
    class RangeFilter extends HTMLElement {
        static get observedAttributes() {
            return ['step', 'filter-values-json', 'filter-active-values-json', 'filter-id'];
        }

        constructor() {
            super();
            this._isReflectingAttributes = false;
            this._sliderLogTimer = null;
            this._sliderLogDebounceMs = 300;

            this.inputOptions = { step: 1, filterId: '', filterLabel: '' };

            this.filterValues = [];
            this.filterValuesMapped = {}; // {valueNumber : value}

            this.filterActiveValues = [];
            this.filterActiveValuesMapped = {}; // {valueNumber : value}

            this.state = { min: null, max: null, low: null, high: null };
            this.elements = {};

            this.handleInputOrChangeEvent = this.handleInputOrChangeEvent.bind(this);
            this.handleNumberFieldKeyDown = this.handleNumberFieldKeyDown.bind(this);
            this.handleNumberFieldBlur = this.handleNumberFieldBlur.bind(this);
        }

        addClearFilterButton() {
            const container = document.getElementById('FacetFiltersActive');
            if (!container) return;

            if (container.querySelector(`.facet-remove[data-filter-id="${this.inputOptions.filterId}"]`)) return;

            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'facet-remove btn btn--outline-grey btn--outline flex relative';
            btn.setAttribute('aria-label', 'Wyczyść');
            btn.setAttribute('is', 'facet-remove');
            btn.setAttribute('data-filter-id', this.inputOptions.filterId);

            btn.innerHTML = `
              <div class="btn__text flex items-center justify-between gap-2">
                <div class="facet-title text-wrap flex items-center gap-2">${this.inputOptions.filterLabel}</div>
                <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"
                  class="icon icon-close icon--extra-small icon--thick" fill="none" viewBox="0 0 24 24">
                  <path d="M18.75 5.13496L5.25 18.8544" stroke="currentColor" stroke-width="1.5"
                    stroke-linecap="round" stroke-linejoin="round"></path>
                  <path d="M18.75 18.8544L5.25 5.13496" stroke="currentColor" stroke-width="1.5"
                    stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
              </div>
            `;

            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');
                if (!form) return;

                // reset wizualny tej instancji suwaka (i tak zostanie przerenderowana po fetchu)
                if (Number.isFinite(this.minimum) && Number.isFinite(this.maximum)) {
                    this.applyValuesToControls(this.minimum, this.maximum, { silent: true });
                }

                // Zbuduj czysty URL bez parametrów tego filtra i wymuś realny re-render.
                // Działa niezależnie od tego, która instancja (drawer/sidebar) jest właścicielem chipa,
                // i niezależnie od update-on-change — dokładnie jak natywny facet-remove.
                const nativeParam = this.inputOptions.filterId.replace(/^range_filter-/, '');
                const url = new URL(window.location.href);
                url.searchParams.delete(nativeParam);                          // natywne wartości (checkboxy)
                url.searchParams.delete(`${this.inputOptions.filterId}_from`); // pamięć suwaka "od"
                url.searchParams.delete(`${this.inputOptions.filterId}_to`);   // pamięć suwaka "do"
                url.searchParams.delete('page');
                url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));

                form.renderSection(url.toString(), e);

                this.emitCustomEvent('rangefilter:change', { low: this.minimum, high: this.maximum });
            });

            container.prepend(btn);
        }

        get minimum() { return this.state.min; }
        get maximum() { return this.state.max; }
        get stepSize() { return this.inputOptions.step; }
        clampToRange(v) {
            if (!Number.isFinite(this.minimum) || !Number.isFinite(this.maximum)) return v;
            return Math.min(this.maximum, Math.max(this.minimum, v));
        }

        initFilterValues() {
            const filterValueNumbers = Object.keys(this.filterValuesMapped);

            const min = Math.floor(Math.min(...filterValueNumbers));
            const max = Math.ceil(Math.max(...filterValueNumbers));

            const setLow = this._getLowFromUrl();
            const setHigh = this._getHighFromUrl();

            let low = setLow ? setLow : min;
            let high = setHigh ? setHigh : max;

            this.setMin(min);
            this.setMax(max);
            this.setLow(Number.isFinite(low) ? low : min);
            this.setHigh(Number.isFinite(high) ? high : max);
        }

        _getLowFromUrl() {
            const urlKey = `${this.inputOptions.filterId}_from`;
            try {
                const params = new URLSearchParams(window.location.search);
                if (!urlKey || !params.has(urlKey)) return null;
                const raw = (params.get(urlKey) ?? '').trim();
                if (raw === '') return null;

                const n = this.numberFromString(raw);
                if (!Number.isFinite(n)) return null;

                return (Number.isFinite(this.minimum) && Number.isFinite(this.maximum))
                    ? this.clampToRange(n)
                    : n;
            } catch {
                return null;
            }
        }

        _getHighFromUrl() {
            const urlKey = `${this.inputOptions.filterId}_to`;
            try {
                const params = new URLSearchParams(window.location.search);
                if (!urlKey || !params.has(urlKey)) return null;
                const raw = (params.get(urlKey) ?? '').trim();
                if (raw === '') return null;

                const n = this.numberFromString(raw);
                if (!Number.isFinite(n)) return null;

                return (Number.isFinite(this.minimum) && Number.isFinite(this.maximum))
                    ? this.clampToRange(n)
                    : n;
            } catch {
                return null;
            }
        }

        _clearInputField(field) {
            if (!field) return;
            field.value = '';
            field.removeAttribute('value');
        }

        _setInputField(field, val) {
            if (!field) return;
            const s = String(val);
            field.value = s;
            field.setAttribute('value', s);
        }

        _mirrorHiddenFields(lowerValue, higherValue) {
            const isLowerEmpty = lowerValue === this.minimum;
            const isHigherEmpty = higherValue === this.maximum;

            if (this.elements.minimumNumberFieldHidden) {
                if (isLowerEmpty) this._clearInputField(this.elements.minimumNumberFieldHidden);
                else this._setInputField(this.elements.minimumNumberFieldHidden, lowerValue);
            }
            if (this.elements.maximumNumberFieldHidden) {
                if (isHigherEmpty) this._clearInputField(this.elements.maximumNumberFieldHidden);
                else this._setInputField(this.elements.maximumNumberFieldHidden, higherValue);
            }
        }

        rangeChanged(source, data) {
            const { low, high } = data;

            const allRangeValueCheckboxes = document.querySelectorAll(
                `input[data-range-filter-id="${this.inputOptions.filterId}"]`
            );
            for (const rangeValueCheckbox of allRangeValueCheckboxes) rangeValueCheckbox.checked = false;

            if (this.minimum === low && this.maximum === high) {
                this._clearInputField(this.elements.minimumNumberField);
                this._clearInputField(this.elements.maximumNumberField);

                this._clearInputField(this.elements.minimumNumberFieldHidden);
                this._clearInputField(this.elements.maximumNumberFieldHidden);

                this.refreshFilterForm();
                return;
            }

            for (const [valueNumber, value] of Object.entries(this.filterValuesMapped)) {
                if (valueNumber >= low && valueNumber <= high) {
                    const filterValueId = this.getFilterValueIdForFilterValue(value);
                    const filterValueCheckboxes = document.querySelectorAll(
                        `input[data-range-filter-value-id="${filterValueId}"]`
                    );
                    for (const rangeValueCheckbox of filterValueCheckboxes) rangeValueCheckbox.checked = true;
                }
            }

            this.refreshFilterForm();
        }

        refreshFilterForm() {
            const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');
            if (!form) return;
            form.querySelector('[name="page"]')?.remove();
            this.dispatchEvent(new Event('change', { bubbles: true }));
        }

        getFilterValueIdForFilterValue(filterValue) {
            return `${this.inputOptions.filterId}-${filterValue}`;
        }

        numberFromString = (s) => {
            const m = String(s).match(/-?\d+(?:[.,]\d+)?/);
            return m ? Number(m[0].replace(',', '.')) : NaN;
        };

        refreshInputOptionsFromAttributes() {
            const s = parseFloat(this.getAttribute('step'));
            this.inputOptions.step = Number.isFinite(s) ? s : this.inputOptions.step;
            this.inputOptions.filterId = this.getAttribute('filter-id') || this.inputOptions.filterId || '';
            this.inputOptions.filterLabel = this.getAttribute('filter-label') || this.inputOptions.filterLabel || '';
        }

        loadFilterJsonData() {
            const parse = (name) => {
                const raw = this.getAttribute(name);
                if (!raw) return [];
                try { return JSON.parse(raw); } catch { return []; }
            };

            this.filterValues = parse('filter-values-json');
            this.filterActiveValues = parse('filter-active-values-json');

            const filterValuesMapped = {};
            for (const filterValue of this.filterValues) {
                const n = this.numberFromString(filterValue);
                if (!Number.isNaN(n) && Number.isFinite(n)) filterValuesMapped[n] = filterValue;
            }
            this.filterValuesMapped = filterValuesMapped;

            const filterActiveValuesMapped = {};
            for (const v of this.filterActiveValues) {
                const n = this.numberFromString(v);
                if (!Number.isNaN(n) && Number.isFinite(n)) filterActiveValuesMapped[n] = v;
            }
            this.filterActiveValuesMapped = filterActiveValuesMapped;
        }

        connectedCallback() {
            this.elements = {
                wrapperElement: this.querySelector('.wrap'),
                lowerSliderElement: this.querySelector('input.low'),
                higherSliderElement: this.querySelector('input.high'),

                minimumNumberField: this.querySelector('.min-input'),
                maximumNumberField: this.querySelector('.max-input'),

                minimumNumberFieldHidden: this.querySelector('.min-input-hidden'),
                maximumNumberFieldHidden: this.querySelector('.max-input-hidden'),
            };

            [this.elements.lowerSliderElement, this.elements.higherSliderElement, this.elements.minimumNumberField, this.elements.maximumNumberField]
                .filter(Boolean).forEach(el => {
                    el.addEventListener('input', this.handleInputOrChangeEvent);
                    el.addEventListener('change', this.handleInputOrChangeEvent);
                });

            [this.elements.minimumNumberField, this.elements.maximumNumberField]
                .filter(Boolean).forEach(el => {
                    el.addEventListener('keydown', this.handleNumberFieldKeyDown, true);
                    el.addEventListener('blur', this.handleNumberFieldBlur);
                });

            this.refreshInputOptionsFromAttributes();
            this.loadFilterJsonData();
            this.initFilterValues();
            this.syncFromAttributes();

            this._onFacetsActiveRender = () => {
                if (!Number.isFinite(this.minimum) || !Number.isFinite(this.maximum)) return;
                const hasValue = !(this.state.low === this.minimum && this.state.high === this.maximum);
                if (hasValue) this.addClearFilterButton();
            };
            document.addEventListener('facets:active:render', this._onFacetsActiveRender);
        }

        disconnectedCallback() {
            [this.elements.lowerSliderElement, this.elements.higherSliderElement, this.elements.minimumNumberField, this.elements.maximumNumberField]
                .filter(Boolean).forEach(el => {
                    el.removeEventListener('input', this.handleInputOrChangeEvent);
                    el.removeEventListener('change', this.handleInputOrChangeEvent);
                });
            [this.elements.minimumNumberField, this.elements.maximumNumberField]
                .filter(Boolean).forEach(el => {
                    el.removeEventListener('keydown', this.handleNumberFieldKeyDown, true);
                    el.removeEventListener('blur', this.handleNumberFieldBlur);
                });

            if (this._onFacetsActiveRender) {
                document.removeEventListener('facets:active:render', this._onFacetsActiveRender);
                this._onFacetsActiveRender = null;
            }
        }

        attributeChangedCallback() {
            if (this._isReflectingAttributes) return;
            if (!this.isConnected) return;
            this.refreshInputOptionsFromAttributes();
            this.loadFilterJsonData();
            this.initFilterValues();
            this.syncFromAttributes();
        }

        get value() {
            return {
                low: parseFloat(this.elements.lowerSliderElement?.value),
                high: parseFloat(this.elements.higherSliderElement?.value)
            };
        }
        set value(v) {
            if (v.low != null) this.setLow(v.low);
            if (v.high != null) this.setHigh(v.high);
        }

        setMin(n) {
            const v = Number(n);
            if (!Number.isFinite(v)) return;
            this.state.min = v;
            if (Number.isFinite(this.state.max) && this.state.max < v) this.state.max = v;
            this.updateControlsMeta();
            const lo = Number.isFinite(this.state.low) ? this.state.low : this.state.min;
            const hi = Number.isFinite(this.state.high) ? this.state.high : this.state.max;
            this.applyValuesToControls(lo, hi, { silent: true });
        }

        setMax(n) {
            const v = Number(n);
            if (!Number.isFinite(v)) return;
            this.state.max = v;
            if (Number.isFinite(this.state.min) && this.state.min > v) this.state.min = v;
            this.updateControlsMeta();
            const lo = Number.isFinite(this.state.low) ? this.state.low : this.state.min;
            const hi = Number.isFinite(this.state.high) ? this.state.high : this.state.max;
            this.applyValuesToControls(lo, hi, { silent: true });
        }

        setLow(n) {
            const lo = Number(n);
            if (!Number.isFinite(lo)) return;
            const hi = Number.isFinite(this.state.high) ? this.state.high : this.state.max;
            this.applyValuesToControls(lo, hi, { silent: false });
        }

        setHigh(n) {
            const hi = Number(n);
            if (!Number.isFinite(hi)) return;
            const lo = Number.isFinite(this.state.low) ? this.state.low : this.state.min;
            this.applyValuesToControls(lo, hi, { silent: false });
        }

        updateControlsMeta() {
            if (!Number.isFinite(this.minimum) || !Number.isFinite(this.maximum)) return;
            const setMeta = el => { if (!el) return; el.min = this.minimum; el.max = this.maximum; el.step = this.stepSize; };
            [this.elements.lowerSliderElement, this.elements.higherSliderElement, this.elements.minimumNumberField, this.elements.maximumNumberField].forEach(setMeta);

            if (this.elements.minimumNumberField) this.elements.minimumNumberField.placeholder = String(this.minimum);
            if (this.elements.maximumNumberField) this.elements.maximumNumberField.placeholder = String(this.maximum);
        }

        syncFromAttributes() {
            if (!Number.isFinite(this.minimum) || !Number.isFinite(this.maximum)) return;
            this.updateControlsMeta();
            let lowerValue = Number.isFinite(this.state.low) ? this.clampToRange(this.state.low) : this.minimum;
            let higherValue = Number.isFinite(this.state.high) ? this.clampToRange(this.state.high) : this.maximum;
            if (lowerValue > higherValue) [lowerValue, higherValue] = [higherValue, lowerValue];
            this.applyValuesToControls(lowerValue, higherValue, { silent: true });
        }

        applyValuesToControls(lowerValue, higherValue, { silent = false } = {}) {
            if (!Number.isFinite(this.minimum) || !Number.isFinite(this.maximum)) return;

            lowerValue = this.clampToRange(lowerValue);
            higherValue = this.clampToRange(Math.max(lowerValue, higherValue));

            if (this.elements.lowerSliderElement) this.elements.lowerSliderElement.value = String(lowerValue);
            if (this.elements.higherSliderElement) this.elements.higherSliderElement.value = String(higherValue);

            if (this.elements.minimumNumberField) {
                if (lowerValue === this.minimum) this._clearInputField(this.elements.minimumNumberField);
                else this._setInputField(this.elements.minimumNumberField, lowerValue);
                this.elements.minimumNumberField.placeholder = String(this.minimum);
            }
            if (this.elements.maximumNumberField) {
                if (higherValue === this.maximum) this._clearInputField(this.elements.maximumNumberField);
                else this._setInputField(this.elements.maximumNumberField, higherValue);
                this.elements.maximumNumberField.placeholder = String(this.maximum);
            }

            this._mirrorHiddenFields(lowerValue, higherValue);

            this.state.low = lowerValue;
            this.state.high = higherValue;

            const span = (this.maximum - this.minimum) || 1;
            const lPct = ((lowerValue - this.minimum) / span) * 100;
            const hPct = ((higherValue - this.minimum) / span) * 100;
            if (this.elements.wrapperElement) {
                this.elements.wrapperElement.style.setProperty('--l', `${lPct}%`);
                this.elements.wrapperElement.style.setProperty('--h', `${hPct}%`);
            }

            if (!silent) this.emitCustomEvent('rangefilter:input', { low: lowerValue, high: higherValue });
        }

        handleInputOrChangeEvent(event) {
            const el = event.currentTarget;

            const fromMinField = el === this.elements.minimumNumberField;
            const fromMaxField = el === this.elements.maximumNumberField;
            const fromField = fromMinField || fromMaxField;
            const fromLow = el === this.elements.lowerSliderElement;
            const fromHigh = el === this.elements.higherSliderElement;

            if (fromField && event.type === 'input') {
                event.preventDefault(); event.stopPropagation();
                return;
            }
            if (fromField && event.type === 'change') {
                this.commitNumberFields(fromMinField ? 'min' : 'max', { fireRangeChanged: false });
                event.preventDefault(); event.stopPropagation();
                return;
            }

            if (fromLow || fromHigh) {
                if (!Number.isFinite(this.minimum) || !Number.isFinite(this.maximum)) return;

                let low = parseFloat(this.elements.lowerSliderElement.value);
                let high = parseFloat(this.elements.higherSliderElement.value);

                if (fromLow) { low = this.clampToRange(low); high = Math.max(low, high); }
                if (fromHigh) { high = this.clampToRange(high); low = Math.min(low, high); }

                this.applyValuesToControls(low, high, { silent: false });

                if (event.type === 'input') {
                } else if (event.type === 'change') {
                    this.flushScheduledRangeChanged();
                    this.rangeChanged(fromLow ? 'slider-low' : 'slider-high', { low, high });
                    this.emitCustomEvent('rangefilter:change', { low, high });
                }

                event.preventDefault(); event.stopPropagation();
            }
        }

        commitNumberFields(source, { fireRangeChanged = true } = {}) {
            if (!Number.isFinite(this.minimum) || !Number.isFinite(this.maximum)) return;

            let rawLow = (this.elements.minimumNumberField?.value ?? '').trim();
            let rawHigh = (this.elements.maximumNumberField?.value ?? '').trim();

            let low = rawLow === '' ? this.minimum : parseFloat(rawLow);
            let high = rawHigh === '' ? this.maximum : parseFloat(rawHigh);

            if (!Number.isFinite(low)) low = this.minimum;
            if (!Number.isFinite(high)) high = this.maximum;
            if (low > high) source === 'min' ? (high = low) : (low = high);

            this.applyValuesToControls(low, high, { silent: false });
            if (fireRangeChanged) {
                this.rangeChanged(source === 'min' ? 'input-min' : 'input-max', { low, high });
                this.emitCustomEvent('rangefilter:change', { low, high });
            }
        }

        handleNumberFieldKeyDown(event) {
            if (event.key !== 'Enter') return;
            const source = event.currentTarget === this.elements.minimumNumberField ? 'min' : 'max';
            this.commitNumberFields(source, { fireRangeChanged: true });
            event.preventDefault(); event.stopPropagation();
        }

        handleNumberFieldBlur(event) {
            const source = event.currentTarget === this.elements.minimumNumberField ? 'min' : 'max';
            this.commitNumberFields(source, { fireRangeChanged: true });
            event.preventDefault(); event.stopPropagation();
        }

        scheduleRangeChanged(source, data) {
            clearTimeout(this._sliderLogTimer);
            this._sliderLogTimer = setTimeout(() => this.rangeChanged(source, data), this._sliderLogDebounceMs);
        }
        flushScheduledRangeChanged() {
            clearTimeout(this._sliderLogTimer);
            this._sliderLogTimer = null;
        }

        emitCustomEvent(type, detail) {
            this.dispatchEvent(new CustomEvent(type, { detail, bubbles: false }));
        }
    }

    customElements.define('range-filter', RangeFilter);
}
